import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "@/features/auth/AuthProvider";
import { DisclaimerGate } from "@/features/legal/DisclaimerGate";

/**
 * SPEC-17 — route guard.
 *
 * Wraps any subtree that should require an authenticated user. While
 * the initial `getSession()` is in flight, renders a blank
 * `bg-background` splash so an already-authed user reloading does NOT
 * flash the login screen before resolving. Unauthed users get
 * redirected to `/login`, with their intended location stashed in
 * navigation state for a future "return-to" feature.
 *
 * SPEC-DOCCONSENT-3b — on the confirmed-user path, the children are
 * wrapped in DisclaimerGate so a logged-in user who has not yet
 * recorded a `document_consents` row for the current disclaimer
 * version is blocked by a modal until they accept. The gate sits
 * INSIDE RequireAuth — never around `/login` — so unauthenticated
 * visitors see the login form without a disclaimer prompt.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="min-h-screen w-full bg-background" aria-hidden />;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <DisclaimerGate>{children}</DisclaimerGate>;
}
